/**
 * modalityPanelCore.js — ЧТО ПОКАЗЫВАТЬ В ПРАВОЙ ПАНЕЛИ ДЛЯ ОТКРЫТОЙ СЕРИИ. [12.0.55]
 *
 * Чистый модуль без импортов, под тестами (tests/modpanel_test.mjs).
 * Проводка на странице — ui/modalityPanel.js.
 *
 * ПОВОД. Владелец: «пользователь просматривает МРТ и не понимает, почему
 * многие кнопки и функции не работают. Нужно оставлять в каждом режиме только
 * те кнопки, которые ему предназначены».
 *
 * Он прав: на МРТ панель предлагала эмфизему, индекс Агатстона, порог в
 * единицах Хаунсфилда, кнопки эхо — и каждая из них на МРТ отказывает или
 * считает чушь. Пустая кнопка в медицинской программе хуже её отсутствия.
 *
 * ПО ЧЕМУ РЕШАЕМ. Только по тегу модальности DICOM (0008,0060) открытой серии.
 * Это факт, записанный аппаратом, а не догадка. Догадку по области тела
 * («КТ грудной клетки») владелец отверг в 12.0.0 — ui/contextUI.js остаётся
 * выключенным, и здесь области тела не угадываем.
 *
 * КАК РАЗМЕЧЕНО. В index.html у заголовка раздела (.section-title), у
 * карточки или у отдельной кнопки стоит data-mod — список модальностей через
 * пробел: «CT», «MR», «CT MR» … Слово ОБЪЁМ — сокращение для «CT MR PT NM»:
 * всё, из чего строится объём (3D, MPR, маска сегментации). Без data-mod —
 * для всех.
 *
 * ТРИ СОСТОЯНИЯ, И ИХ НЕЛЬЗЯ СМЕШИВАТЬ (тот же урок, что 12.0.35 у пресетов):
 *   null         — исследования нет: показываем ВСЁ (смотреть, что умеет
 *                  программа, можно и до открытия снимка);
 *   'неизвестно' — серия открыта, а тега модальности нет (или заглушка OT/?):
 *                  тоже ВСЁ — не знаем, значит не отнимаем;
 *   группа       — 'CT', 'MR', 'PT', 'NM', 'US', 'XR', 'MG', 'XA' или 'иное'.
 * 'иное' — модальность известна, но ни одна размеченная кнопка ей не
 * предназначена (снимок экрана SC, фото, эндоскопия): остаются общие.
 *
 * EXPORTS: группаМодальности, разобратьМетку, видимоДля, ОБЪЁМ, ГРУППЫ,
 *          НЕИЗВЕСТНО, ПОДПИСЬ_ГРУППЫ
 */

/** Серия открыта, модальность не записана. */
export const НЕИЗВЕСТНО = 'неизвестно';

/** Все группы, которые может носить data-mod. */
export const ГРУППЫ = Object.freeze(['CT', 'MR', 'PT', 'NM', 'US', 'XR', 'MG', 'XA']);

/** Из чего строится объём: 3D, MPR, маска, отсечение, слэб. */
export const ОБЪЁМ = Object.freeze(['CT', 'MR', 'PT', 'NM']);

/** Как назвать группу врачу. */
export const ПОДПИСЬ_ГРУППЫ = Object.freeze({
  CT: 'КТ', MR: 'МРТ', PT: 'ПЭТ', NM: 'сцинтиграфия/ОФЭКТ', US: 'УЗИ',
  XR: 'рентген', MG: 'маммография', XA: 'ангиография', иное: 'эта серия',
});

/**
 * Код DICOM → группа. Синонимы одной съёмки сведены вместе: CR, DX, RG, PX,
 * IO — это всё рентгенограмма; RF и DS — рентгеноскопия и старое имя
 * субтракции, они идут с XA; IVUS — ультразвук.
 */
const _КАРТА = {
  CT: 'CT',
  MR: 'MR',
  PT: 'PT',
  NM: 'NM',
  US: 'US', IVUS: 'US',
  CR: 'XR', DX: 'XR', RG: 'XR', PX: 'XR', IO: 'XR', XR: 'XR', DR: 'XR',
  MG: 'MG',
  XA: 'XA', RF: 'XA', DS: 'XA',
};

/**
 * Значения, которые загрузчик подставляет сам, когда тега нет: 'OT'
 * (workerPool) и '?' (сводка серий loaderManager). Для правила это пустой
 * тег — тот же выбор, что в presetModalityCore.ЗАГЛУШКИ.
 */
const _ЗАГЛУШКИ = new Set(['', 'OT', '?']);

/**
 * Модальность ОТКРЫТОЙ серии → группа. Никогда не null: null означает
 * «исследования нет», и его передаёт только проводка.
 * @param {string|null|undefined} dicom — значение (0008,0060), бывает «CT\\PT»
 * @returns {string} 'CT'|'MR'|'PT'|'NM'|'US'|'XR'|'MG'|'XA'|'иное'|'неизвестно'
 */
export function группаМодальности(dicom) {
  // Многозначный тег и «CT/PT» сводок: берём первое значение — так серию
  // показывает и подпись модальности на снимке.
  const м = String(dicom ?? '').toUpperCase().split(/[\\/,]/)[0].trim();
  if (_ЗАГЛУШКИ.has(м)) return НЕИЗВЕСТНО;
  return _КАРТА[м] || 'иное';
}

/**
 * Разобрать data-mod в набор групп. ОБЪЁМ раскрывается в CT MR PT NM.
 * Неизвестное слово — ошибка разметки: бросаем, чтобы тест её поймал, а не
 * чтобы кнопка молча пропала навсегда.
 * @param {string|null|undefined} метка
 * @returns {Set<string>|null} null — метки нет (кнопка для всех)
 */
export function разобратьМетку(метка) {
  const слова = String(метка ?? '').trim().toUpperCase().split(/\s+/).filter(Boolean);
  if (!слова.length) return null;
  const набор = new Set();
  for (const с of слова) {
    if (с === 'ОБЪЁМ' || с === 'ОБЪЕМ') { for (const г of ОБЪЁМ) набор.add(г); continue; }
    if (!ГРУППЫ.includes(с)) throw new Error(`data-mod: неизвестная модальность «${с}»`);
    набор.add(с);
  }
  return набор;
}

/**
 * Видна ли кнопка (раздел, карточка) с такой меткой при такой серии.
 * @param {string|null|undefined} метка — data-mod; пусто — для всех
 * @param {string|null} группа — из группаМодальности; null — исследования нет
 */
export function видимоДля(метка, группа) {
  if (группа === null || группа === undefined) return true;   // исследования нет — всё
  if (группа === НЕИЗВЕСТНО) return true;                     // не знаем — не отнимаем
  const набор = разобратьМетку(метка);
  if (!набор) return true;                                     // общая кнопка
  return набор.has(группа);
}
